import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-theme-auswaehlen";
const TITLE = "Shopify Theme auswählen: kostenlos, Premium oder eigenes Theme?";
const DESCRIPTION =
  "Wie Sie ein Shopify Theme auswählen, das zum Sortiment passt: Unterschiede zwischen Gratis- und Premium-Themes, Grenzen im Alltag und wann ein eigenes Theme sinnvoll ist.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Für die meisten Shops reicht ein gepflegtes Standard-Theme wie Dawn oder ein Premium-Theme aus dem Shopify Theme Store, ergänzt um gezielte Anpassungen. Ein eigenes Theme lohnt sich erst, wenn Darstellung oder Bestellprozess so speziell sind, dass jedes Standard-Theme dauerhaft dagegen arbeitet. Entscheidend ist nicht die Optik der Demo, sondern wie gut das Theme Ihr Sortiment abbildet.";

const faqs = [
  {
    q: "Reicht ein kostenloses Shopify Theme?",
    a: "Für viele Shops ja. Dawn und die weiteren kostenlosen Themes von Shopify sind schnell, werden gepflegt und lassen sich anpassen. Premium-Themes bringen vor allem fertige Bausteine mit, die man sonst nachbauen müsste.",
  },
  {
    q: "Was kostet ein Premium-Theme?",
    a: "Themes im Shopify Theme Store liegen üblicherweise im mittleren dreistelligen Bereich als Einmalzahlung. Der eigentliche Aufwand steckt danach in Einrichtung, Inhalten und Anpassung.",
  },
  {
    q: "Wann lohnt sich ein individuelles Theme?",
    a: "Wenn Konfiguratoren, komplexe Variantenlogik, B2B-Bestellwege oder ein eigenes Designsystem gefordert sind. Unterhalb dieser Anforderungen ist ein angepasstes Standard-Theme günstiger und leichter zu warten.",
  },
  {
    q: "Kann ich das Theme später wechseln?",
    a: "Ja, Produkte und Bestellungen bleiben erhalten, sie liegen unabhängig vom Theme im Shop. Aufwand entsteht durch Inhalte, individuelle Anpassungen und App-Einbindungen, die im neuen Theme erneut eingebaut werden müssen.",
  },
];

export const Route = createFileRoute("/blog/shopify-theme-auswaehlen")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Themes",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Themes"
      title="Shopify Theme auswählen"
      intro="Die Theme-Auswahl entscheidet über Monate an Folgearbeit. Wer nach der schönsten Demo geht, merkt oft erst mit echten Produkten, dass die Bausteine nicht zum Sortiment passen. Es lohnt sich, die Auswahl an den eigenen Inhalten zu prüfen statt an Beispielbildern."
      source="blog-shopify-theme"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Kostenlos oder Premium",
          p: [
            "Die kostenlosen Themes von Shopify sind technisch sauber, schnell und werden fortlaufend aktualisiert. Sie bringen weniger fertige Bausteine mit, was in der Praxis oft ein Vorteil ist: Weniger Optionen bedeuten weniger Ballast im Code.",
            "Premium-Themes lohnen sich, wenn sie Bausteine mitbringen, die Sie sonst nachbauen lassen müssten, etwa Bundle-Auswahl, Vergleichstabellen, Lookbook-Ansichten oder eine ausgefeilte Filterlogik.",
          ],
        },
        {
          h: "Prüfen Sie mit echten Produkten",
          p: [
            "Demos zeigen fünf perfekte Produkte mit gleich großen Bildern. Ihr Sortiment sieht anders aus. Testen Sie das Theme mit Ihren längsten Produktnamen, den meisten Varianten, dem schlechtesten Bild und der längsten Beschreibung.",
            "Prüfen Sie außerdem die mobile Ansicht der Produktseite und der Sammlungsseite. Dort entsteht der Großteil des Umsatzes und dort scheitern viele Themes an langen Titeln und vielen Varianten.",
          ],
          list: [
            "Produktseite mit maximaler Variantenzahl",
            "Sammlungsseite mit Filtern und vielen Produkten",
            "Warenkorb und Zahlartenanzeige",
            "Suchergebnisse mit Tippfehlern",
            "Ladeverhalten auf dem Mobiltelefon",
          ],
        },
        {
          h: "Wann ein eigenes Theme",
          p: [
            "Ein eigenes Theme ist berechtigt, wenn Standardstrukturen dauerhaft im Weg stehen: Produktkonfiguratoren, Bestellwege für Geschäftskunden, Sortimente mit sehr vielen Attributen oder ein festes Designsystem aus dem Markenauftritt.",
            "Der Preis ist Wartung. Ein individuelles Theme erhält keine Updates vom Anbieter, jede Weiterentwicklung von Shopify müssen Sie selbst nachziehen. Das ist vertretbar, wenn der Shop diese Sonderlogik wirklich braucht.",
          ],
        },
        {
          h: "Nach der Auswahl beginnt die Arbeit",
          p: [
            "Ein Theme ist ein Rahmen, kein fertiger Shop. Der Unterschied zwischen einem durchschnittlichen und einem überzeugenden Shopify-Shop entsteht danach: durch Reihenfolge auf der Produktseite, klare Versandinformationen, gute Bilder und einen Checkout ohne Überraschungen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-theme-anpassung", label: "Shopify Theme Anpassung" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shopify Shop erstellen lassen" },
        { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseite optimieren" },
      ]}
    />
  );
}
